import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Building2,
  Users,
  FileClock,
  Palette,
  BellRing,
  ShieldCheck,
  Save,
  Loader2,
  Check,
  ArrowRight,
  Copy,
  Sparkles,
  Info,
  Mail,
  Phone,
  Globe,
  MapPin,
  FileText,
  BadgeCheck,
  UserPlus,
  UserCheck,
  KeyRound,
  Layers,
  Search,
  Filter,
  Building,
  ClipboardCheck,
  CalendarClock,
  Timer,
  Radio,
  ShieldAlert,
  Archive,
  History,
  RefreshCw,
} from "lucide-react";
import AppShell from "../AppShell";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import {
  PageHeader,
  Card,
  Button,
  StatusPill,
  StatCard,
  ErrorBanner,
  SuccessBanner,
  Kicker,
  EmptyState,
  LoadingRow,
} from "../../components/primitives";

/* ------------------------------------------------------------------
 * Customer-operational sections. Nothing here configures CanniComply
 * the platform — every section helps the customer run their own
 * compliance operation.
 * ------------------------------------------------------------------ */
const SECTIONS = [
  {
    id: "organisation",
    label: "Organisation",
    icon: Building2,
    kicker: "Profile",
    description: "Legal name, address and primary contact details for your business.",
  },
  {
    id: "users",
    label: "Users & Roles",
    icon: Users,
    kicker: "Access",
    description: "Owners and staff who can sign in, and the permissions attached to each role.",
  },
  {
    id: "departments",
    label: "Departments",
    icon: Building,
    kicker: "Structure",
    description: "The departments in use across your team and their headcount.",
  },
  {
    id: "compliance",
    label: "Compliance settings",
    icon: ClipboardCheck,
    kicker: "Policy",
    description: "Pass marks, retest cadence and how long staff have before an assignment is flagged overdue.",
  },
  {
    id: "certificates",
    label: "Certificate validity",
    icon: BadgeCheck,
    kicker: "Renewals",
    description: "How long a certificate is valid for and when your team is warned about upcoming expiries.",
  },
  {
    id: "reminders",
    label: "Reminder settings",
    icon: BellRing,
    kicker: "Signals",
    description: "When we nudge staff about outstanding work and when the owner digest lands.",
  },
  {
    id: "retention",
    label: "Evidence retention",
    icon: Archive,
    kicker: "Records",
    description: "How long we retain signed records, acknowledgements and audit events for inspection.",
  },
  {
    id: "audit",
    label: "Audit log",
    icon: FileClock,
    kicker: "Trail",
    description: "Immutable, searchable log of every change of record inside your workspace.",
  },
];

/* Defaults used when compliance_settings / notification_prefs have not
 * yet been persisted for this tenant. These match the values enforced
 * elsewhere in the backend today, so the Admin area shows the customer
 * what CanniComply is actually doing on their behalf. */
const COMPLIANCE_DEFAULTS = {
  pass_mark_pct: 66,
  retest_cooldown_hours: 0,
  overdue_after_days: 14,
  auto_lock_after_days: 30,
  reassessment_on_document_revision: true,
};
const CERTIFICATE_DEFAULTS = {
  validity_months: 12,
  warn_before_days: 30,
  block_expired: true,
};
const REMINDER_DEFAULTS = {
  first_nudge_after_days: 3,
  cadence_days: 7,
  owner_digest_after_days: 7,
  channels: { email: true, in_app: true, slack: false },
  quiet_hours: { start: "18:00", end: "08:00" },
};
const RETENTION_INFO = {
  certificates_years: 7,
  acknowledgements_years: 7,
  audit_events_years: 7,
  removed_staff_tombstone: true,
};

export default function Admin() {
  const { section } = useParams();
  const navigate = useNavigate();
  const active = SECTIONS.find((s) => s.id === section) || SECTIONS[0];
  const { business } = useAuth();
  const isDemo = business?.is_demo;

  return (
    <AppShell>
      <PageHeader
        kicker="Administration"
        title="Run your compliance operation"
        description="Everything you need to keep your organisation inspection-ready — your people, your departments, your policies and your evidence trail."
        testid="admin-page-header"
        action={
          isDemo ? (
            <StatusPill tone="gold" icon={Sparkles}>
              Demo — read only
            </StatusPill>
          ) : (
            <StatusPill tone="emerald" icon={Radio}>
              Live workspace
            </StatusPill>
          )
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-[240px,1fr] gap-6">
        <AdminNav active={active.id} onSelect={(id) => navigate(`/app/admin/${id}`)} />
        <div className="min-w-0" data-testid={`admin-section-${active.id}`}>
          <AdminSectionHeader section={active} />
          <div className="mt-6">
            {active.id === "organisation" && <OrganisationSection isDemo={isDemo} />}
            {active.id === "users" && <UsersSection isDemo={isDemo} />}
            {active.id === "departments" && <DepartmentsSection />}
            {active.id === "compliance" && <ComplianceSettingsSection isDemo={isDemo} />}
            {active.id === "certificates" && <CertificateValiditySection isDemo={isDemo} />}
            {active.id === "reminders" && <ReminderSettingsSection isDemo={isDemo} />}
            {active.id === "retention" && <RetentionSection />}
            {active.id === "audit" && <AuditSection />}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/* ---------------------- Left-rail navigation ---------------------- */
function AdminNav({ active, onSelect }) {
  return (
    <nav
      data-testid="admin-nav"
      className="rounded-2xl border border-slate-200 bg-white p-2 md:sticky md:top-24 self-start"
    >
      <ul className="space-y-0.5">
        {SECTIONS.map((s) => {
          const isActive = active === s.id;
          const Icon = s.icon;
          return (
            <li key={s.id}>
              <button
                data-testid={`admin-nav-${s.id}`}
                onClick={() => onSelect(s.id)}
                className={`w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] transition-colors border ${
                  isActive
                    ? "bg-[#0B1220] text-[#F0D497] border-[#0B1220]"
                    : "bg-transparent hover:bg-slate-50 text-slate-700 border-transparent hover:border-slate-200"
                }`}
              >
                <span
                  className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${
                    isActive
                      ? "bg-[#D4A65A]/20 text-[#F0D497]"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <Icon size={13} />
                </span>
                <span className="min-w-0 flex-1">
                  <div className="font-semibold">{s.label}</div>
                  <div
                    className={`text-[10.5px] uppercase tracking-[0.16em] ${
                      isActive ? "text-[#F0D497]/70" : "text-slate-400"
                    }`}
                  >
                    {s.kicker}
                  </div>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function AdminSectionHeader({ section }) {
  const Icon = section.icon;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-start gap-4">
      <span className="h-11 w-11 rounded-xl bg-[#0B1220] text-[#D4A65A] flex items-center justify-center shrink-0">
        <Icon size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <Kicker tone="gold">{section.kicker}</Kicker>
        <h2 className="mt-1 font-display text-2xl md:text-[26px] tracking-tight text-slate-900 font-semibold">
          {section.label}
        </h2>
        <p className="mt-1 text-[13.5px] text-slate-500 leading-relaxed max-w-2xl">
          {section.description}
        </p>
      </div>
    </div>
  );
}

/* ================================================================
 *  1 · Organisation
 * ================================================================ */
function OrganisationSection({ isDemo }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/business")
      .then((b) => {
        if (!alive) return;
        setForm({
          name: b.name || "",
          industry: b.industry || "",
          address: b.address || "",
          contact_email: b.contact_email || "",
          contact_phone: b.contact_phone || "",
          website: b.website || "",
          country: b.country || "",
          registration_number: b.registration_number || "",
        });
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await api.patch("/business", form);
      setNotice("Organisation profile saved. Changes appear on new certificates and reports immediately.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingRow label="Loading organisation profile…" testid="admin-org-loading" />;
  if (!form) return null;

  return (
    <div className="space-y-5" data-testid="admin-organisation">
      <ErrorBanner error={error} title="We couldn't save your changes" />
      <SuccessBanner notice={notice} testid="admin-org-notice" />

      <Card padding="lg">
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <Field
            label="Legal business name"
            icon={Building2}
            testid="admin-org-name"
            value={form.name}
            onChange={(v) => update("name", v)}
            placeholder="Acme Compliance Ltd"
          />
          <Field
            label="Industry"
            icon={Layers}
            value={form.industry}
            onChange={(v) => update("industry", v)}
            placeholder="Medical cannabis, Pharma manufacturing, …"
          />
          <Field
            label="Registration number"
            icon={FileText}
            value={form.registration_number}
            onChange={(v) => update("registration_number", v)}
            placeholder="Companies House / VAT / GPhC"
          />
          <Field
            label="Country"
            icon={Globe}
            value={form.country}
            onChange={(v) => update("country", v)}
            placeholder="United Kingdom"
          />
        </div>
      </Card>

      <Card padding="lg">
        <SectionTitle icon={MapPin} title="Contact" description="How inspectors, staff and auto-emails should reach you." />
        <div className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-5">
          <Field
            label="Primary contact email"
            icon={Mail}
            type="email"
            value={form.contact_email}
            onChange={(v) => update("contact_email", v)}
            placeholder="compliance@yourbusiness.com"
          />
          <Field
            label="Phone"
            icon={Phone}
            value={form.contact_phone}
            onChange={(v) => update("contact_phone", v)}
            placeholder="+44 …"
          />
          <Field
            label="Website"
            icon={Globe}
            value={form.website}
            onChange={(v) => update("website", v)}
            placeholder="https://yourbusiness.com"
          />
          <div className="md:col-span-2">
            <FieldLabel icon={MapPin}>Registered address</FieldLabel>
            <textarea
              data-testid="admin-org-address"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              rows={3}
              placeholder={"Line 1\nCity\nPostcode"}
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[13.5px] text-slate-900 focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20 transition-colors"
            />
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {isDemo && (
          <div className="text-[12px] text-amber-700">Demo workspace is read-only.</div>
        )}
        <Button
          variant="accent"
          icon={saving ? Loader2 : Save}
          onClick={save}
          disabled={saving || isDemo}
          data-testid="admin-org-save"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

/* ================================================================
 *  2 · Users & Roles
 * ================================================================ */
function UsersSection({ isDemo }) {
  const [owners, setOwners] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([api.get("/admin/owners"), api.get("/team")])
      .then(([o, t]) => {
        if (!alive) return;
        setOwners(o || []);
        setStaff(t || []);
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const invitePending = staff.filter((m) => m.invite_status === "pending").length;
  const activatedStaff = staff.filter((m) => m.invite_status === "active").length;

  return (
    <div className="space-y-5" data-testid="admin-users">
      <ErrorBanner error={error} title="Users could not be loaded" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Owners" value={owners.length} icon={KeyRound} tone="gold" testid="admin-stat-owners" />
        <StatCard label="Staff activated" value={activatedStaff} icon={UserCheck} tone="emerald" testid="admin-stat-staff-active" />
        <StatCard label="Invites pending" value={invitePending} icon={Mail} tone="amber" testid="admin-stat-invites" />
        <StatCard label="Total people" value={owners.length + staff.length} icon={Users} testid="admin-stat-seats" />
      </div>

      <Card padding="lg">
        <div className="flex items-center justify-between gap-3">
          <SectionTitle
            icon={KeyRound}
            title="Owners"
            description="Full access to every workspace, policy and evidence record."
          />
          <StatusPill tone="gold" icon={ShieldCheck}>Highest privilege</StatusPill>
        </div>

        {loading ? (
          <LoadingRow label="Loading owners…" />
        ) : owners.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title="No owners on record"
            description="This workspace has no owner accounts registered. Contact support if you see this."
          />
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {owners.map((o) => (
              <li key={o.id} data-testid={`admin-owner-${o.id}`} className="flex items-center gap-4 p-4">
                <span className="h-9 w-9 rounded-lg bg-[#0B1220] text-[#D4A65A] flex items-center justify-center text-[11px] font-semibold shrink-0">
                  {(o.name || o.email || "").slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] text-slate-900 font-semibold truncate">
                    {o.name || o.email}
                  </div>
                  <div className="text-[11.5px] text-slate-500 font-mono truncate">{o.email}</div>
                </div>
                <StatusPill tone="gold">Owner</StatusPill>
                <span className="hidden md:inline-block text-[11px] text-slate-400 font-mono">
                  Added {formatDate(o.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card padding="lg">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SectionTitle
            icon={Users}
            title="Staff"
            description="People you have invited into your workspace. Manage individual profiles, roles and invites from the Team page."
          />
          <Button variant="secondary" size="sm" icon={UserPlus} to="/app/team" data-testid="admin-goto-team">
            Manage staff
          </Button>
        </div>
        {loading ? (
          <LoadingRow label="Loading staff…" />
        ) : staff.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title="No staff yet"
            description="Invite your first team member to start assigning documents and competencies."
            action={<Button variant="accent" size="md" icon={UserPlus} to="/app/team">Invite staff</Button>}
          />
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {staff.slice(0, 8).map((m) => {
              const status = m.invite_status || "unknown";
              const tone = status === "active" ? "emerald" : status === "pending" ? "amber" : "slate";
              const label = status === "active" ? "Activated" : status === "pending" ? "Invite pending" : "Not invited";
              return (
                <li key={m.id} className="flex items-center gap-4 p-4">
                  <span className="h-9 w-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center text-[11px] font-semibold shrink-0">
                    {(m.name || "").slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] text-slate-900 font-semibold truncate">{m.name}</div>
                    <div className="text-[11.5px] text-slate-500 truncate">
                      {m.role || m.job_title || "Team member"}
                      {m.department ? ` · ${m.department}` : ""}
                    </div>
                  </div>
                  <StatusPill tone={tone}>{label}</StatusPill>
                </li>
              );
            })}
            {staff.length > 8 && (
              <li className="p-3.5 text-center bg-slate-50/60">
                <Link to="/app/team" className="text-[12.5px] font-semibold text-[#8C6B33] hover:text-[#0B1220] inline-flex items-center gap-1.5">
                  See all {staff.length} team members <ArrowRight size={12} />
                </Link>
              </li>
            )}
          </ul>
        )}
      </Card>

      <Card padding="lg" tone="subtle">
        <SectionTitle
          icon={Info}
          title="Role model"
          description="Two roles, strictly enforced. Every action mutating your records is checked at the API before it runs."
        />
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <RoleTile
            name="Owner"
            tone="gold"
            icon={KeyRound}
            bullets={[
              "Full admin nav — Dashboard, Team, Documents, Learning, Reports, Administration",
              "Invites, edits and removes team members",
              "Uploads, revises and assigns SOPs and competencies",
              "Sees every certificate and audit event",
            ]}
          />
          <RoleTile
            name="Staff"
            tone="slate"
            icon={UserCheck}
            bullets={[
              "Sees only My training and My certificates",
              "Cannot access any owner surface",
              "Signs documents and completes assessments assigned to them",
              "Receives an activation invite by email",
            ]}
          />
        </div>
      </Card>
    </div>
  );
}

function RoleTile({ name, tone, icon: Icon, bullets }) {
  const chip =
    tone === "gold"
      ? "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40"
      : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2.5">
        <span className={`h-8 w-8 rounded-lg border flex items-center justify-center ${chip}`}>
          <Icon size={14} />
        </span>
        <div className="font-display text-[15px] text-slate-900 font-semibold">{name}</div>
      </div>
      <ul className="mt-3 space-y-1.5 text-[12.5px] text-slate-600 leading-relaxed">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2">
            <Check size={12} className="mt-1 text-emerald-600 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ================================================================
 *  3 · Departments
 * ================================================================ */
function DepartmentsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/departments")
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return <LoadingRow label="Loading departments…" />;
  const departments = data?.departments || [];
  const unassigned = data?.unassigned || 0;
  const total = data?.total_staff || 0;

  return (
    <div className="space-y-5" data-testid="admin-departments">
      <ErrorBanner error={error} title="Departments could not be loaded" />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Departments in use" value={departments.length} icon={Building} testid="admin-stat-departments" />
        <StatCard label="Staff assigned" value={total - unassigned} icon={Users} tone="emerald" />
        <StatCard label="Unassigned staff" value={unassigned} icon={UserPlus} tone={unassigned > 0 ? "amber" : "slate"} />
      </div>

      <Card padding="lg">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SectionTitle
            icon={Building}
            title="Department roster"
            description="Departments are set per team member on the Team page. They're used to scope document assignments and roll-up compliance reporting by area."
          />
          <Button variant="secondary" size="sm" icon={Users} to="/app/team">
            Edit on Team
          </Button>
        </div>

        {departments.length === 0 ? (
          <EmptyState
            icon={Building}
            title="No departments yet"
            description="Give each team member a department (Clinical, Dispensary, QA, Warehouse…) on the Team page. They'll roll up here automatically."
            action={<Button variant="accent" size="md" icon={Users} to="/app/team">Open Team</Button>}
          />
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {departments.map((d) => (
              <li key={d.name} className="p-4 flex items-center gap-4">
                <span className="h-10 w-10 rounded-lg bg-[#FDF6E7] text-[#8C6B33] border border-[#D4A65A]/40 flex items-center justify-center shrink-0">
                  <Building size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] text-slate-900 font-semibold truncate">{d.name}</div>
                  {d.roles?.length > 0 && (
                    <div className="text-[11.5px] text-slate-500 truncate">
                      {d.roles.slice(0, 4).join(" · ")}
                      {d.roles.length > 4 && ` +${d.roles.length - 4} more`}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-display text-[18px] text-slate-900 font-semibold tabular-nums">{d.headcount}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">people</div>
                </div>
              </li>
            ))}
            {unassigned > 0 && (
              <li className="p-4 flex items-center gap-4 bg-amber-50/40">
                <span className="h-10 w-10 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 flex items-center justify-center shrink-0">
                  <UserPlus size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] text-amber-900 font-semibold">Not yet assigned to a department</div>
                  <div className="text-[11.5px] text-amber-700">These staff aren't caught by any department-based document scope.</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-[18px] text-amber-900 font-semibold tabular-nums">{unassigned}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-amber-700 font-semibold">people</div>
                </div>
              </li>
            )}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* ================================================================
 *  4 · Compliance settings
 * ================================================================ */
function ComplianceSettingsSection({ isDemo }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/business")
      .then((b) => {
        if (!alive) return;
        setForm({ ...COMPLIANCE_DEFAULTS, ...(b.compliance_settings || {}) });
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await api.patch("/business", { compliance_settings: form });
      setNotice("Compliance policy updated. New assignments and assessments follow these rules from now on.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingRow label="Loading compliance policy…" />;
  if (!form) return null;

  return (
    <div className="space-y-5" data-testid="admin-compliance">
      <ErrorBanner error={error} title="We couldn't save your policy" />
      <SuccessBanner notice={notice} />

      <Card padding="lg">
        <SectionTitle
          icon={ClipboardCheck}
          title="Assessment policy"
          description="How your team is graded when they complete an assessment linked to a document."
        />
        <div className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-5">
          <NumberField
            label="Pass mark"
            hint="Percentage required to pass an assessment"
            unit="%"
            min={50}
            max={100}
            step={1}
            value={form.pass_mark_pct}
            onChange={(v) => update("pass_mark_pct", v)}
            testid="admin-passmark"
          />
          <NumberField
            label="Retest cooldown"
            hint="Wait this long after a failed attempt before retesting"
            unit="hours"
            min={0}
            max={168}
            step={1}
            value={form.retest_cooldown_hours}
            onChange={(v) => update("retest_cooldown_hours", v)}
          />
        </div>
      </Card>

      <Card padding="lg">
        <SectionTitle
          icon={Timer}
          title="Overdue policy"
          description="When an assignment gets flagged as overdue and eventually escalated."
        />
        <div className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-5">
          <NumberField
            label="Flag as overdue"
            hint="An unfinished assignment turns amber after this many days"
            unit="days"
            min={1}
            max={90}
            step={1}
            value={form.overdue_after_days}
            onChange={(v) => update("overdue_after_days", v)}
            testid="admin-overdue-days"
          />
          <NumberField
            label="Auto-escalate to owner"
            hint="Include in the owner overdue digest after this many days"
            unit="days"
            min={1}
            max={180}
            step={1}
            value={form.auto_lock_after_days}
            onChange={(v) => update("auto_lock_after_days", v)}
          />
        </div>
      </Card>

      <Card padding="lg" tone="subtle">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-lg bg-white border border-slate-200 text-[#8C6B33] flex items-center justify-center shrink-0">
            <RefreshCw size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] text-slate-900 font-semibold">Reset staff on document revision</div>
            <div className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">
              When you push a new version of an SOP, previously completed
              competency assignments linked to it flip back to <b>in progress</b>
              so affected staff must re-attest against the current version.
              Recommended for regulated operations.
            </div>
          </div>
          <SwitchInput
            checked={form.reassessment_on_document_revision}
            onChange={(v) => update("reassessment_on_document_revision", v)}
          />
        </div>
      </Card>

      <SavePolicyRow isDemo={isDemo} saving={saving} onSave={save} testid="admin-compliance-save" />
    </div>
  );
}

/* ================================================================
 *  5 · Certificate validity
 * ================================================================ */
function CertificateValiditySection({ isDemo }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/business")
      .then((b) => {
        if (!alive) return;
        const current = { ...(b.compliance_settings || {}) };
        setForm({
          ...CERTIFICATE_DEFAULTS,
          validity_months: current.certificate_validity_months ?? CERTIFICATE_DEFAULTS.validity_months,
          warn_before_days: current.certificate_warn_before_days ?? CERTIFICATE_DEFAULTS.warn_before_days,
          block_expired: current.block_expired_certificates ?? CERTIFICATE_DEFAULTS.block_expired,
          _existing: current,
        });
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const compliance_settings = {
        ...(form._existing || {}),
        certificate_validity_months: form.validity_months,
        certificate_warn_before_days: form.warn_before_days,
        block_expired_certificates: form.block_expired,
      };
      await api.patch("/business", { compliance_settings });
      setNotice("Certificate validity settings saved. New certificates use these rules from now on.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingRow label="Loading certificate policy…" />;
  if (!form) return null;

  return (
    <div className="space-y-5" data-testid="admin-certificates">
      <ErrorBanner error={error} title="We couldn't save your policy" />
      <SuccessBanner notice={notice} />

      <Card padding="lg">
        <SectionTitle
          icon={CalendarClock}
          title="Validity window"
          description="How long a certificate remains valid before staff must recertify."
        />
        <div className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-5">
          <NumberField
            label="Certificate valid for"
            hint="Recommended: 12 months for regulated operations"
            unit="months"
            min={1}
            max={60}
            step={1}
            value={form.validity_months}
            onChange={(v) => update("validity_months", v)}
            testid="admin-validity-months"
          />
          <NumberField
            label="Warn before expiry"
            hint="How early you and the certificate holder see an expiry warning"
            unit="days"
            min={7}
            max={180}
            step={1}
            value={form.warn_before_days}
            onChange={(v) => update("warn_before_days", v)}
          />
        </div>
      </Card>

      <Card padding="lg" tone="subtle">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-lg bg-white border border-slate-200 text-[#8C6B33] flex items-center justify-center shrink-0">
            <ShieldAlert size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] text-slate-900 font-semibold">Block work with an expired certificate</div>
            <div className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">
              When a certificate expires, the linked competency assignment
              flips back to <b>needs re-attestation</b>. Staff can still see
              the historic certificate for evidence — but they're prompted
              to renew before the next assignment counts as compliant.
            </div>
          </div>
          <SwitchInput
            checked={form.block_expired}
            onChange={(v) => update("block_expired", v)}
          />
        </div>
      </Card>

      <SavePolicyRow isDemo={isDemo} saving={saving} onSave={save} testid="admin-certificates-save" />
    </div>
  );
}

/* ================================================================
 *  6 · Reminder settings
 * ================================================================ */
function ReminderSettingsSection({ isDemo }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/business")
      .then((b) => {
        if (!alive) return;
        const prefs = b.notification_prefs || {};
        setForm({
          first_nudge_after_days: prefs.first_nudge_after_days ?? REMINDER_DEFAULTS.first_nudge_after_days,
          cadence_days: prefs.cadence_days ?? REMINDER_DEFAULTS.cadence_days,
          owner_digest_after_days: prefs.owner_digest_after_days ?? REMINDER_DEFAULTS.owner_digest_after_days,
          email: prefs.channels?.email ?? REMINDER_DEFAULTS.channels.email,
          in_app: prefs.channels?.in_app ?? REMINDER_DEFAULTS.channels.in_app,
          quiet_start: prefs.quiet_hours?.start ?? REMINDER_DEFAULTS.quiet_hours.start,
          quiet_end: prefs.quiet_hours?.end ?? REMINDER_DEFAULTS.quiet_hours.end,
        });
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const notification_prefs = {
        first_nudge_after_days: form.first_nudge_after_days,
        cadence_days: form.cadence_days,
        owner_digest_after_days: form.owner_digest_after_days,
        channels: { email: form.email, in_app: form.in_app },
        quiet_hours: { start: form.quiet_start, end: form.quiet_end },
      };
      await api.patch("/business", { notification_prefs });
      setNotice("Reminder policy saved. New reminders use this cadence immediately.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingRow label="Loading reminder policy…" />;
  if (!form) return null;

  return (
    <div className="space-y-5" data-testid="admin-reminders">
      <ErrorBanner error={error} title="We couldn't save your reminders" />
      <SuccessBanner notice={notice} />

      <Card padding="lg">
        <SectionTitle
          icon={BellRing}
          title="Staff reminders"
          description="When outstanding assignments are nudged and how often the reminder repeats."
        />
        <div className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-5">
          <NumberField
            label="First nudge after"
            hint="Wait this long from the assignment date before the first reminder"
            unit="days"
            min={1}
            max={30}
            step={1}
            value={form.first_nudge_after_days}
            onChange={(v) => update("first_nudge_after_days", v)}
            testid="admin-first-nudge"
          />
          <NumberField
            label="Repeat every"
            hint="Interval between subsequent reminders while work is still outstanding"
            unit="days"
            min={1}
            max={30}
            step={1}
            value={form.cadence_days}
            onChange={(v) => update("cadence_days", v)}
          />
        </div>
      </Card>

      <Card padding="lg">
        <SectionTitle
          icon={CalendarClock}
          title="Owner digest"
          description="How aggressively work is escalated to you as the owner."
        />
        <div className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-5">
          <NumberField
            label="Include in owner digest after"
            hint="Anything still outstanding after this many days appears in the weekly digest"
            unit="days"
            min={1}
            max={30}
            step={1}
            value={form.owner_digest_after_days}
            onChange={(v) => update("owner_digest_after_days", v)}
          />
          <div>
            <FieldLabel icon={Timer}>Quiet hours</FieldLabel>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="time"
                value={form.quiet_start}
                onChange={(e) => update("quiet_start", e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20"
              />
              <span className="text-[12px] text-slate-500">to</span>
              <input
                type="time"
                value={form.quiet_end}
                onChange={(e) => update("quiet_end", e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20"
              />
            </div>
            <div className="mt-1.5 text-[11.5px] text-slate-500">
              Emails aren't sent during this window — they queue and dispatch when quiet hours end.
            </div>
          </div>
        </div>
      </Card>

      <Card padding="lg" tone="subtle">
        <SectionTitle icon={Mail} title="Channels" description="How your reminders reach the people who need to act." />
        <div className="mt-4 space-y-3">
          <ChannelRow
            label="Email"
            hint="Sent to the staff member's registered email address"
            checked={form.email}
            onChange={(v) => update("email", v)}
          />
          <ChannelRow
            label="In-app banner"
            hint="Shown on their dashboard the next time they sign in"
            checked={form.in_app}
            onChange={(v) => update("in_app", v)}
          />
        </div>
      </Card>

      <SavePolicyRow isDemo={isDemo} saving={saving} onSave={save} testid="admin-reminders-save" />
    </div>
  );
}

function ChannelRow({ label, hint, checked, onChange }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3.5 flex items-center gap-3">
      <span className="h-8 w-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
        <Mail size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-slate-900 font-semibold">{label}</div>
        <div className="text-[11.5px] text-slate-500">{hint}</div>
      </div>
      <SwitchInput checked={checked} onChange={onChange} />
    </div>
  );
}

/* ================================================================
 *  7 · Evidence retention
 * ================================================================ */
function RetentionSection() {
  return (
    <div className="space-y-5" data-testid="admin-retention">
      <Card padding="lg">
        <SectionTitle
          icon={Archive}
          title="Retention policy"
          description="How long each record type is retained inside your workspace. These minimums are enforced by CanniComply and can't be shortened — if you need longer retention for a specific regulator, ask us."
        />
        <div className="mt-5 grid md:grid-cols-3 gap-3">
          <RetentionTile
            label="Certificates"
            years={RETENTION_INFO.certificates_years}
            icon={BadgeCheck}
            hint="Signed records of every passed assessment"
          />
          <RetentionTile
            label="Acknowledgements"
            years={RETENTION_INFO.acknowledgements_years}
            icon={ClipboardCheck}
            hint="Every policy read + tick + sign event"
          />
          <RetentionTile
            label="Audit events"
            years={RETENTION_INFO.audit_events_years}
            icon={FileClock}
            hint="Every user, assignment and document change"
          />
        </div>
      </Card>

      <Card padding="lg">
        <SectionTitle
          icon={ShieldCheck}
          title="How your evidence is protected"
          description="Design principles enforced across the platform, not per-tenant toggles."
        />
        <ul className="mt-4 grid md:grid-cols-2 gap-3 text-[13px] text-slate-700">
          {[
            "Certificates are SHA-256 signed at issue and never mutated.",
            "The audit log is append-only — records can never be edited or removed.",
            "Signed records are retained even after a staff member is removed (tombstoned on their name).",
            "Backups run daily with a 30-day rolling window.",
            "Every acknowledgement captures signature, timestamp and viewing device.",
            "Data lives in the EU (Ireland region) and is encrypted at rest.",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white p-3">
              <Check size={13} className="text-emerald-600 mt-0.5 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card padding="lg" tone="subtle">
        <SectionTitle
          icon={History}
          title="Staff who leave your organisation"
          description="What happens to a person's evidence when you remove them from the Team page."
        />
        <ul className="mt-3 space-y-2 text-[12.5px] text-slate-600 leading-relaxed">
          <li className="flex gap-2"><Check size={13} className="text-emerald-600 mt-0.5 shrink-0" /><span>Outstanding assignments are removed — you don't want them counting against your compliance score.</span></li>
          <li className="flex gap-2"><Check size={13} className="text-emerald-600 mt-0.5 shrink-0" /><span>Historic certificates are retained under the person's name for the full retention window and remain in your Audit Log and Reports.</span></li>
          <li className="flex gap-2"><Check size={13} className="text-emerald-600 mt-0.5 shrink-0" /><span>The staff user account is signed out and its invite links revoked immediately.</span></li>
        </ul>
      </Card>
    </div>
  );
}

function RetentionTile({ label, years, icon: Icon, hint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2.5">
        <span className="h-9 w-9 rounded-lg bg-[#0B1220] text-[#D4A65A] flex items-center justify-center">
          <Icon size={14} />
        </span>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display text-3xl text-slate-900 font-semibold tabular-nums">{years}</span>
        <span className="text-[12px] text-slate-500 font-semibold">years minimum</span>
      </div>
      <div className="mt-1 text-[11.5px] text-slate-500">{hint}</div>
    </div>
  );
}

/* ================================================================
 *  8 · Audit log
 * ================================================================ */
function AuditSection() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/audit-log?limit=200")
      .then((rows) => alive && setEvents(rows || []))
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const kinds = useMemo(() => {
    const set = new Set(events.map((e) => e.kind));
    return ["all", ...Array.from(set)];
  }, [events]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return events.filter((e) => {
      if (kind !== "all" && e.kind !== kind) return false;
      if (!term) return true;
      return [e.label, e.actor, e.target, e.detail, e.reference]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(term));
    });
  }, [events, kind, q]);

  return (
    <div className="space-y-5" data-testid="admin-audit">
      <ErrorBanner error={error} title="Audit trail could not be loaded" />

      <Card padding="lg">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SectionTitle
            icon={FileClock}
            title="Immutable event trail"
            description="Every certificate issued, assignment made and account created is logged. Read-only by design — inspectors can rely on it."
          />
          <StatusPill tone="emerald" icon={ShieldCheck}>
            SHA-256 · retained 7y
          </StatusPill>
        </div>

        <div className="mt-4 grid md:grid-cols-[1fr,220px] gap-3">
          <label className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="admin-audit-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search actor, target, module, certificate id…"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 bg-white text-[13px] focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20"
            />
          </label>
          <label className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              data-testid="admin-audit-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 bg-white text-[13px] focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20 appearance-none"
            >
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {k === "all" ? "All events" : k.replace(/\./g, " · ")}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <LoadingRow label="Loading audit trail…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileClock}
            title="No events match your filters"
            description="Adjust the search or kind filter to see more of your trail."
          />
        ) : (
          <ul className="mt-5 space-y-1">
            {filtered.slice(0, 100).map((e) => (
              <AuditRow key={e.id} event={e} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function AuditRow({ event }) {
  const kindMeta = {
    "certificate.issued": { icon: BadgeCheck, tone: "emerald" },
    "assignment.created": { icon: FileText, tone: "gold" },
    "competency.assigned": { icon: Sparkles, tone: "gold" },
    "user.created": { icon: UserPlus, tone: "slate" },
    "user.activated": { icon: UserCheck, tone: "emerald" },
    "document.uploaded": { icon: FileText, tone: "slate" },
  };
  const meta = kindMeta[event.kind] || { icon: FileClock, tone: "slate" };
  const Icon = meta.icon;
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    gold: "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <li className="grid grid-cols-[auto,1fr,auto] items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50/60 transition-colors border border-transparent hover:border-slate-100">
      <span className={`h-8 w-8 rounded-lg border flex items-center justify-center shrink-0 ${toneMap[meta.tone]}`}>
        <Icon size={13} />
      </span>
      <div className="min-w-0">
        <div className="text-[13px] text-slate-900 truncate">
          <b>{event.label}</b>
          <span className="text-slate-500 font-normal">
            {" "}· {event.actor} → {event.target}
          </span>
        </div>
        <div className="text-[11.5px] text-slate-500 truncate">
          {event.detail}
          {event.reference && (
            <span className="text-slate-400 font-mono"> · {event.reference}</span>
          )}
        </div>
      </div>
      <div className="text-[10.5px] font-mono text-slate-400 whitespace-nowrap">
        {formatDate(event.at)}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------
 * Shared building blocks
 * ------------------------------------------------------------------ */
function Field({ label, icon: Icon, testid, value, onChange, placeholder, type = "text", disabled }) {
  return (
    <label className="block">
      <FieldLabel icon={Icon}>{label}</FieldLabel>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[13.5px] text-slate-900 focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
      />
    </label>
  );
}

function NumberField({ label, hint, unit, min, max, step, value, onChange, testid }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-1.5 flex items-stretch rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:border-[#D4A65A] focus-within:ring-2 focus-within:ring-[#D4A65A]/20">
        <input
          data-testid={testid}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 min-w-0 px-3.5 py-2.5 text-[14px] text-slate-900 focus:outline-none tabular-nums"
        />
        <span className="px-3 flex items-center bg-slate-50 border-l border-slate-200 text-[11.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">
          {unit}
        </span>
      </div>
      {hint && <div className="mt-1.5 text-[11.5px] text-slate-500 leading-relaxed">{hint}</div>}
    </div>
  );
}

function FieldLabel({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
      {Icon && <Icon size={11} />}
      {children}
    </span>
  );
}

function SectionTitle({ icon: Icon, title, description }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-[#8C6B33]" />}
        <h3 className="font-display text-[15px] text-slate-900 font-semibold">{title}</h3>
      </div>
      {description && (
        <p className="mt-1 text-[12.5px] text-slate-500 leading-relaxed max-w-2xl">{description}</p>
      )}
    </div>
  );
}

function SwitchInput({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      className={`inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
        checked ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span
        className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function SavePolicyRow({ isDemo, saving, onSave, testid }) {
  return (
    <div className="flex items-center justify-end gap-3">
      {isDemo && (
        <div className="text-[12px] text-amber-700">Demo workspace is read-only.</div>
      )}
      <Button
        variant="accent"
        icon={saving ? Loader2 : Save}
        onClick={onSave}
        disabled={saving || isDemo}
        data-testid={testid}
      >
        {saving ? "Saving…" : "Save policy"}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "";
    return d.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
